import _defineProperty from "/build/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/build/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from "react";
import { createContext, useReducer, useMemo, useRef, useEffect, useCallback } from "react";
import classnames from "classnames";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { useRouter } from "next/router";
import { useModal } from "@/hooks";
import isOuterAreaClick from "@/lib/isOuterAreaClick";
import styles from "@/styles/Modal.module.scss";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var IconClose = function IconClose(props) {
  return /*#__PURE__*/_jsxs("svg", _objectSpread(_objectSpread({}, props), {}, {
    children: [/*#__PURE__*/_jsx("rect", {
      width: "48",
      height: "48",
      fill: "rgba(255,255,255,0)"
    }), /*#__PURE__*/_jsx("path", {
      d: "M12,11.293,22.293,1,23,1.707,12.707,12,23,22.293,22.293,23,12,12.707,1.707,23,1,22.293,11.293,12,1,1.707,1.707,1Z",
      transform: "translate(12 12)",
      fill: "#909090",
      fillRule: "evenodd"
    })]
  }));
};

IconClose.defaultProps = {
  xmlns: "http://www.w3.org/2000/svg",
  width: "48",
  height: "48",
  viewBox: "0 0 48 48"
};
export var ModalContext = /*#__PURE__*/createContext({
  showModal: function showModal() {
    return null;
  },
  hideModal: function hideModal() {
    return null;
  }
});
ModalContext.displayName = "ModalContext";
var id = 0;

function generateId() {
  return id++;
}

var initialValue = [];

var reducer = function reducer(state, action) {
  var newState = _toConsumableArray(state);

  switch (action.type) {
    case "add":
      newState.push(action.value);
      return newState;

    case "remove":
      newState.pop();
      return newState;

    default:
      return state;
  }
};

export function ModalProvider(_ref) {
  var children = _ref.children;

  var _useReducer = useReducer(reducer, initialValue),
      modals = _useReducer[0],
      dispatch = _useReducer[1];

  var showModal = useCallback(function (modalComponent) {
    var modalProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    dispatch({
      type: "add",
      value: {
        component: modalComponent,
        props: modalProps,
        key: "modal-".concat(generateId())
      }
    });
  }, []);
  var hideModal = useCallback(function () {
    dispatch({
      type: "remove"
    });
  }, []);
  var contextValue = useMemo(function () {
    return {
      showModal: showModal,
      hideModal: hideModal
    };
  }, []);
  return /*#__PURE__*/_jsxs(ModalContext.Provider, {
    value: contextValue,
    children: [children, modals.map(function (modal) {
      return /*#__PURE__*/_jsx(Modal, _objectSpread(_objectSpread({}, modal.props), {}, {
        children: modal.component
      }), modal.key);
    })]
  });
}
export default function Modal(_ref2) {
  var _ref2$style = _ref2.style,
      style = _ref2$style === void 0 ? "medium" : _ref2$style,
      _ref2$closeButton = _ref2.closeButton,
      closeButton = _ref2$closeButton === void 0 ? true : _ref2$closeButton,
      _ref2$outerAreaClose = _ref2.outerAreaClose,
      outerAreaClose = _ref2$outerAreaClose === void 0 ? true : _ref2$outerAreaClose,
      onClose = _ref2.onClose,
      children = _ref2.children;
  var modalStyles = useMemo(function () {
    if (typeof style === "string") {
      return styles[style];
    }

    if (Array.isArray(style)) {
      return style.map(function (s) {
        return styles[s];
      }).join(" ");
    }
  }, [style]);
  var screen = useRef();
  var content = useRef();

  var _useModal = useModal(),
      hideModal = _useModal.hideModal;

  var router = useRouter();
  useEffect(function () {
    disableBodyScroll(screen.current);
    return function () {
      clearAllBodyScrollLocks();
    };
  }, []);
  var close = useCallback(function (e) {
    if (onClose) {
      onClose(e);
    }

    enableBodyScroll(screen.current);
    hideModal();
  }, []);
  var onScreenClick = useCallback(function (e) {
    if (!outerAreaClose) return false;

    if (isOuterAreaClick(content.current, e)) {
      close(e);
    }
  });
  useEffect(function () {
    router.events.on("routeChangeComplete", close);
    return function () {
      router.events.off("routeChangeComplete", close);
    };
  }, [close]);
  return /*#__PURE__*/_jsx("div", {
    className: styles.modal,
    ref: screen,
    onClick: onScreenClick,
    children: /*#__PURE__*/_jsxs("div", {
      className: classnames(styles.content, modalStyles),
      ref: content,
      children: [closeButton && /*#__PURE__*/_jsx("button", {
        className: styles.close,
        tabIndex: "0",
        role: "button",
        onClick: close,
        children: /*#__PURE__*/_jsx(IconClose, {})
      }), children]
    })
  });
}